// ---------------------------------------------------------------------------
// Turning a rendered document into pixels a thermal printer can take.
//
// WHY THIS RUNS IN THE BROWSER.
//
// The receipt and the preparation ticket are React components. They are the
// only description of those documents that exists, and they run here. The
// browser has already done the hard parts - shaped the Arabic, ordered the
// right-to-left runs, laid out the mixed Arabic and English, drawn the logo
// and the borders - so we photograph what it drew.
//
// The alternative would be a second renderer on the server, which drifts from
// the designers the first time somebody changes one and not the other, or a
// headless browser, which is a hundred megabytes of Chromium on a butcher's
// till. Neither is worth it when the pixels are already on screen.
//
// WHAT COMES OUT is one byte per pixel, non-zero meaning BLACK. The backend
// packs those into ESC/POS bits. One byte per pixel rather than packed bits
// here because the packing has a bit order that is easy to get backwards, and
// it belongs in one place with a test around it.
//
// THE DEPENDENCY IS LOADED LAZILY. `html-to-image` is only needed at the
// moment somebody prints, and the POS must not carry it in the bundle it
// loads to take the first sale of the day.
// ---------------------------------------------------------------------------

/// A rendered document, ready to be sent.
export interface RasterPayload {
  width: number;
  height: number;
  /// One BIT per pixel, MSB first, each row padded to a whole byte - the
  /// same layout the printer's `GS v 0` command consumes, base64 for the
  /// wire.
  ///
  /// This used to be one BYTE per pixel, which put a 576-dot receipt at
  /// 576 bytes a row and ran into the server's body limit at about 1,350
  /// rows - roughly fifteen items. The sale completed, the receipt did not
  /// print, and the message said only that sending failed. Packing is the
  /// fix, not a bigger limit: the raster is monochrome, so seven of every
  /// eight bytes said nothing.
  bitsBase64: string;
}

/// Guard rails, matched to the backend's own limits.
///
/// A receipt taller than this is a runaway loop rather than a document, and
/// sending it would tie up the printer for minutes.
const MAX_WIDTH = 1024;
const MAX_HEIGHT = 20000;

/// Above this, a pixel is treated as white.
///
/// 176 rather than a middle 128: thermal paper has no greys, and anti-aliased
/// text thresholded at the midpoint comes out thin and broken. Leaning towards
/// black keeps small Arabic diacritics and the thin dividers legible.
const BLACK_THRESHOLD = 176;

export class RasterError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RasterError';
  }
}

/// Photograph one element and reduce it to black and white.
///
/// `element` must be the SHEET ITSELF - the `.receipt-sheet` or
/// `.ticket-sheet` - and not its frame. Passing a wrapper captures the grey
/// preview background, the zoom buttons and the panel border, all of which
/// would be printed as a black rectangle around the receipt.
export async function rasteriseElement(
  element: HTMLElement,
  options: { pixelWidth: number } ,
): Promise<RasterPayload> {
  const { pixelWidth } = options;

  if (!Number.isInteger(pixelWidth) || pixelWidth < 8 || pixelWidth > MAX_WIDTH) {
    throw new RasterError('That paper width is not one this system can print.');
  }

  // Loaded here, not at the top of the file: the POS bundle must not carry a
  // rasteriser to take the first sale of the day.
  const { toCanvas } = await import('html-to-image');

  const onScreenWidth = element.offsetWidth;
  if (onScreenWidth <= 0) {
    throw new RasterError('There is nothing on screen to print.');
  }

  // Scale so the captured image is EXACTLY the printer's dot width. Capturing
  // at screen size and stretching afterwards is what makes thermal output look
  // blurry: the printer has no greys to interpolate with.
  const scale = pixelWidth / onScreenWidth;

  let canvas: HTMLCanvasElement | null = null;

  try {
    canvas = await toCanvas(element, {
      // A clean white sheet. Without this the page background - which may be
      // the grey preview frame - is baked in and prints as a black field.
      backgroundColor: '#ffffff',
      pixelRatio: scale,
      // Skip anything explicitly marked as screen-only, so a control that
      // happens to sit inside the sheet is never photographed.
      filter: (node) =>
        !(node instanceof HTMLElement && node.classList?.contains('no-print')),
    });

    if (canvas.width > MAX_WIDTH || canvas.height > MAX_HEIGHT) {
      throw new RasterError('That document is too large to print.');
    }

    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) throw new RasterError('This browser cannot prepare the document for printing.');

    const image = context.getImageData(0, 0, canvas.width, canvas.height);

    // Thresholded and PACKED in one pass: each row is ceil(width/8) bytes,
    // MSB first, so bit 7 of byte 0 is the leftmost pixel. Building the
    // byte-per-pixel array first and packing second would briefly hold both
    // - on a 100-item receipt that is an extra half-megabyte for nothing.
    const bytesPerRow = Math.ceil(canvas.width / 8);
    const packed = new Uint8Array(bytesPerRow * canvas.height);

    for (let y = 0; y < canvas.height; y += 1) {
      const rowStart = y * bytesPerRow;
      for (let x = 0; x < canvas.width; x += 1) {
        const i = (y * canvas.width + x) * 4;
        const alpha = image.data[i + 3]!;
        // Fully transparent is paper, not ink - whatever colour it claims.
        if (alpha < 16) continue;

        // Perceptual luminance. A flat average turns red text into mid-grey
        // and loses it at the threshold; this keeps colour text black.
        const luminance =
          0.299 * image.data[i]! + 0.587 * image.data[i + 1]! + 0.114 * image.data[i + 2]!;

        if (luminance < BLACK_THRESHOLD) {
          packed[rowStart + (x >> 3)]! |= 0x80 >> (x & 7);
        }
      }
    }

    return {
      width: canvas.width,
      height: canvas.height,
      bitsBase64: toBase64(packed),
    };
  } finally {
    // A canvas holds its backing store until it is collected, and a till that
    // prints two hundred receipts a day would accumulate two hundred of them.
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }
}

/// Bytes to base64, without a dependency and without blowing the stack.
///
/// `String.fromCharCode(...bytes)` on a 700,000-element array throws
/// "Maximum call stack size exceeded" - which on a till appears as a receipt
/// that will not print and no explanation.
function toBase64(bytes: Uint8Array): string {
  const CHUNK = 0x8000;
  let binary = '';

  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }

  return btoa(binary);
}

/// The dot width for a roll.
///
/// The PRINTABLE width, not the paper width: an 80 mm roll prints 72 mm, which
/// at 8 dots/mm is 576. Using the paper figure produces a receipt 11% too wide
/// that wraps its right-hand column - and the right-hand column is the total.
export function dotsForRoll(widthMm: 58 | 80): number {
  return widthMm === 58 ? 384 : 576;
}
